//TODO: move this TS to core data access lib?
import { CoachType, GpiStateModel, GroupType, RecoveryStateModel } from '@goatsports/core/data-access';
import { HubConnection, IStreamResult } from '@microsoft/signalr';

export class EventsHub {
  constructor(private connection: HubConnection) {}

  registerCallbacks(implementation: IEventsHubCallbacks) {
    this.connection.on('MessageCreated', (model) => implementation.messageCreated(model));
    this.connection.on('UndeliveredMessageCreated', (model) => implementation.undeliveredMessageCreated(model));
    this.connection.on('UserConnectionReestablished', (model) => implementation.userConnectionReestablished(model));
    this.connection.on('MessageDeleted', (model) => implementation.messageDeleted(model));
    this.connection.on('MessageRead', (model) => implementation.messageRead(model));
    this.connection.on('UserConnectionStatusChanged', (model) => implementation.userConnectionStatusChanged(model));
    this.connection.on('UserJoinedGroup', (model) => implementation.userJoinedGroup(model));
    this.connection.on('UserLeftGroup', (model) => implementation.userLeftGroup(model));
    this.connection.on('UserRemovedFromGroup', (model) => implementation.userRemovedFromGroup(model));
    this.connection.on('WorkoutSubmitted', (model) => implementation.workoutSubmitted(model));
    this.connection.on('NotificationCreated', (model) => implementation.notificationCreated(model));
    this.connection.on('GroupMessageCountsChanged', (model) => implementation.groupMessageCountsChanged(model));
    this.connection.on('NotificationCountsChanged', (model) => implementation.notificationCountsChanged(model));
    this.connection.on('CommentCreated', (model) => implementation.commentCreated(model));
    this.connection.on('CommentDeleted', (model) => implementation.commentDeleted(model));
    this.connection.on('CommentCountsChanged', (model) => implementation.commentCountsChanged(model));
    this.connection.on('CoachWorkoutTrackingStarted', (model) => implementation.coachWorkoutTrackingStarted(model));
    this.connection.on('CoachWorkoutTrackingStopped', (model) => implementation.coachWorkoutTrackingStopped(model));
    this.connection.on('ActiveOrganizationChanged', (model) => implementation.activeOrganizationChanged?.(model));
    this.connection.on('RecoveryUpdated', (model) => implementation.recoveryUpdated?.(model));
    this.connection.on('GpiUpdated', (model) => implementation.gpiUpdated?.(model));
  }

  unregisterCallbacks(implementation: IEventsHubCallbacks) {
    this.connection.off('MessageCreated', (model) => implementation.messageCreated(model));
    this.connection.off('UndeliveredMessageCreated', (model) => implementation.undeliveredMessageCreated(model));
    this.connection.off('UserConnectionReestablished', (model) => implementation.userConnectionReestablished(model));
    this.connection.off('MessageDeleted', (model) => implementation.messageDeleted(model));
    this.connection.off('MessageRead', (model) => implementation.messageRead(model));
    this.connection.off('UserConnectionStatusChanged', (model) => implementation.userConnectionStatusChanged(model));
    this.connection.off('UserJoinedGroup', (model) => implementation.userJoinedGroup(model));
    this.connection.off('UserLeftGroup', (model) => implementation.userLeftGroup(model));
    this.connection.off('UserRemovedFromGroup', (model) => implementation.userRemovedFromGroup(model));
    this.connection.off('WorkoutSubmitted', (model) => implementation.workoutSubmitted(model));
    this.connection.off('NotificationCreated', (model) => implementation.notificationCreated(model));
    this.connection.off('GroupMessageCountsChanged', (model) => implementation.groupMessageCountsChanged(model));
    this.connection.off('NotificationCountsChanged', (model) => implementation.notificationCountsChanged(model));
    this.connection.off('CommentCreated', (model) => implementation.commentCreated(model));
    this.connection.off('CommentDeleted', (model) => implementation.commentDeleted(model));
    this.connection.off('CommentCountsChanged', (model) => implementation.commentCountsChanged(model));
    this.connection.off('CoachWorkoutTrackingStarted', (model) => implementation.coachWorkoutTrackingStarted(model));
    this.connection.off('CoachWorkoutTrackingStopped', (model) => implementation.coachWorkoutTrackingStopped(model));
    this.connection.off('ActiveOrganizationChanged', (model) => implementation.activeOrganizationChanged?.(model));
    this.connection.off('RecoveryUpdated', (model) => implementation.recoveryUpdated?.(model));
    this.connection.off('GpiUpdated', (model) => implementation.gpiUpdated?.(model));
  }
}

export interface IEventsHubCallbacks {
  messageCreated(model: UserMessageModel): void;
  undeliveredMessageCreated(model: UndeliveredMessageCreatedEvent): void;
  userConnectionReestablished(model: UserConnectionReestablishedEvent): void;
  messageDeleted(model: MessageEventModel): void;
  messageRead(model: MessageEventModel): void;
  userConnectionStatusChanged(model: UserConnectionStatusChangedEvent): void;
  userJoinedGroup(model: GroupEventModel): void;
  userLeftGroup(model: GroupEventModel): void;
  userRemovedFromGroup(model: UserRemovedFromGroupEvent): void;
  workoutSubmitted(model: WorkoutEvent): void;
  notificationCreated(model: NotificationCreatedEvent): void;
  groupMessageCountsChanged(model: GroupMessageCountsChangedEvent): void;
  notificationCountsChanged(model: NotificationCountsChangedEvent): void;
  commentCreated(model: CommentModel): void;
  commentDeleted(model: CommentDeletedEventModel): void;
  commentCountsChanged(model: CommentCountsChangedEventModel): void;
  coachWorkoutTrackingStarted(model: CoachWorkoutTrackingEventModel): void;
  coachWorkoutTrackingStopped(model: CoachWorkoutTrackingEventModel): void;
  // Optional: only coach-portal listens; the apps that don't implement it ignore the event.
  activeOrganizationChanged?(model: ActiveOrganizationChangedEventModel): void;
  // Optional: only client-app listens. The day's state, as GET Me/Recovery would answer.
  recoveryUpdated?(model: RecoveryStateModel): void;
  // Optional: only client-app listens. A recalculated day's GPI, and the days it may change.
  gpiUpdated?(model: GpiUpdatedEvent): void;
}

// Mirrors the BE CoachWorkoutTrackingEventModel (camelCase over the wire;
// enums serialize as strings). Started carries expiresUTC — the lock lapses at
// that timestamp unless a heartbeat re-emits Started with a later one — plus
// activity (what the coach is doing, so the client shows the right locked copy).
// Stopped carries reason: 'Submitted' means the workout is now Done (refetch
// it), 'Cancelled' means it simply unlocked.
export interface CoachWorkoutTrackingEventModel {
  userUid: string;
  userWorkoutUid: string;
  expiresUTC: string | undefined;
  activity: CoachTrackingActivity;
  reason: CoachTrackingStopReason | undefined;
}

// What the coach holding the lock is doing. Both activities take the same lock
// (tracking and editing are mutually exclusive); the client uses this to choose
// the "tracked by coach" vs "edited by coach" locked state.
export enum CoachTrackingActivity {
  Track = 'Track',
  Edit = 'Edit',
}

export enum CoachTrackingStopReason {
  Cancelled = 'Cancelled',
  Submitted = 'Submitted',
  // Coach untracked (unsubmitted) the workout — its status reverted; refetch
  // like Submitted.
  Reverted = 'Reverted',
}

// Activity-feed comment events. Payloads mirror the BE CommentDto /
// CommentDeletedEvent / CommentCountsChangedEvent (camelCase over the wire).
export interface CommentModel {
  uid: string;
  sourceEventUid: string;
  groupUid: string;
  text: string;
  createdAtUtc: string;
  authorUid: string;
  authorFirstName: string | undefined;
  authorLastName: string | undefined;
  canDelete: boolean;
}

export interface CommentDeletedEventModel {
  sourceEventUid: string;
  groupUid: string;
  commentUid: string;
}

export interface CommentCountsChangedEventModel {
  userUid: string | undefined;
  sourceEventUid: string;
  groupUid: string;
  groupCount: number;
  hasUnread: boolean;
}

export interface UserConnectionReestablishedEvent {
  userUid: string | undefined;
  groupUid: string | undefined;
  status: ConnectionStatusType | undefined;
  groupType: GroupType | undefined;
}

export interface UndeliveredMessageCreatedEvent {
  model: UserMessageModel;
  groupUid: string | undefined;
  userUid: string | undefined;
  data: string | undefined;
}

export interface UserMessageModel {
  uid: string | undefined;
  messageStatusType: MessageStatusType | undefined;
  message: MessageModel | undefined;
  replyToMessage: MessageModel | undefined;
}

export enum MessageStatusType {
  Unknown = 0,
  New = 1,
  Read = 2,
}

export interface MessageModel {
  uid: string | undefined;
  text: string | undefined;
  senderRole: RoleType;
  createdDateTimeUTC: string;
  userWorkoutUid: string | undefined;
  userReflectionEventUid: string | undefined;
  senderUser: MessageSenderModel | undefined;
  groupUid: string | undefined;
}

export enum RoleType {
  Unknown = 'Unknown',
  Coach = 'Coach',
  CustomClient = 'CustomClient',
  PremiumClient = 'PremiumClient',
  System = 'System',
  FreemiumClient = 'FreemiumClient',
}

export interface MessageSenderModel {
  uid: string | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
  role: RoleType;
  connectionStatusType: ConnectionStatusType | undefined;
}

export enum ConnectionStatusType {
  Unknown = 'Unknown',
  Offline = 'Offline',
  Online = 'Online',
}

export interface MessageEventModel {
  groupUid: string | undefined;
  messageUid: string | undefined;
}

export interface UserConnectionStatusChangedEvent {
  userUid: string | undefined;
  status: ConnectionStatusType;
}

export interface GroupEventModel {
  messageUid: string | undefined;
  groupUid: string | undefined;
  text: string | undefined;
}

export interface WorkoutEvent {
  userFirstName: string | undefined;
  userLastName: string | undefined;
  userUid: string | undefined;
  userWorkoutUid: string | undefined;
  notificationUid: string | undefined;
  messageUid: string | undefined;
  groupUid: string | undefined;
  text: string | undefined;
  createdDateTimeUTC: Date;
}

export interface NotificationCreatedEvent {
  uid: string | undefined;
  coachUid: string | undefined;
  clientUid: string | undefined;
  clientFirstName: string | undefined;
  clientLastName: string | undefined;
  createdDateTimeUTC: string;
  type: NotificationType;
  payload: NotificationPayload | undefined;
  payloadJson: string | undefined;
}

export enum NotificationType {
  Unknown = 'Unknown',
  WorkoutMissed = 'WorkoutMissed',
  WorkoutMoved = 'WorkoutMoved',
  MessageCreated = 'MessageCreated',
  ReflectionSubmissionReminder = 'ReflectionSubmissionReminder',
}

export interface NotificationPayload {
  groupUid: string | undefined;
  messageUid: string | undefined;
  userWorkoutUid: string | undefined;
  workoutName: string | undefined;
}

export interface GroupMessageCountsChangedEvent {
  userUid: string | undefined;
  groupUid: string | undefined;
  groupUnreadCount: number;
  totalUnreadCount: number;
}

export interface NotificationCountsChangedEvent {
  userUid: string | undefined;
  unreadCount: number;
}

export interface UserRemovedFromGroupEvent {
  model: GroupEventModel | undefined;
  userUid: string | undefined;
}

// Mirrors the BE ActiveOrganizationChangedEventModel: pushed to every connection of the admin
// whose active organization was switched, so their other open portal tabs can react.
export interface ActiveOrganizationChangedEventModel {
  userUid: string;
  organizationUid: string;
}

// Mirrors the BE GpiUpdated message, sent each time a (client, day) is recalculated, beside
// RecoveryUpdated. `gpi` is `localDate`'s state as GET Me/Gpi would answer; every day from
// `affectedFrom` (the day before) to today may have a changed GPI.
export interface GpiUpdatedEvent {
  localDate: string;
  affectedFrom: string;
  gpi: GpiStateModel;
}
